import { useState, useMemo } from 'react'
import { Crown, ChevronRight, CheckCircle2, Circle, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { tournamentsAPI } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ParticipantName } from './BracketTreeView'

/* Ladder / King of the Hill view — huidige king + match, admin-knop
   "Volgende uitdager", historiek van eerdere matches. */
export default function LadderBracket({ tournamentId, matches, participantMap, participantHrefs = {}, onMatchClick, isAdmin }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const sorted = useMemo(
    () => matches.slice().sort((a, b) => a.match_number - b.match_number),
    [matches]);
  const last = sorted[sorted.length - 1]
  const lastDone = last?.status === 'completed'

  const callNext = async () => {
    setBusy(true); setError(null)
    try { await tournamentsAPI.ladderNextChallenger(tournamentId) }
    catch (e) { setError(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  if (!last) {
    return <div className="text-center py-12 text-muted-foreground">Nog geen ladder-matches — start het toernooi eerst.</div>
  }

  const currentKing = lastDone ? last.winner_id : last.participant1_id
  const currentChallenger = lastDone ? null : last.participant2_id

  return (
    <div className="space-y-6">
      {/* Huidige king */}
      <Card className="border-gold/40">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-gold">
            <Crown className="h-4 w-4" /> Huidige king
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-xl font-bold">
            <ParticipantName name={participantMap[currentKing] || '—'} href={participantHrefs[currentKing]} />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {sorted.filter(m => m.winner_id === currentKing).length} match{sorted.filter(m => m.winner_id === currentKing).length === 1 ? '' : 'es'} gewonnen
          </div>
        </CardContent>
      </Card>

      {/* Huidige match */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-widest">
            Match {last.match_number}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div onClick={() => isAdmin && !lastDone && onMatchClick && onMatchClick(last)}
               className={cn('grid grid-cols-[1fr_auto_1fr] items-center gap-3 p-3 rounded border border-border',
                             isAdmin && !lastDone && 'cursor-pointer hover:border-acid')}>
            <div className={cn('text-center', !!last.winner_id && last.winner_id === last.participant1_id && 'text-live font-bold')}>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">King</div>
              <div className="text-lg">
                <ParticipantName name={participantMap[last.participant1_id] || '?'} href={participantHrefs[last.participant1_id]} />
              </div>
            </div>
            <div className="text-muted-foreground">vs</div>
            <div className={cn('text-center', !!last.winner_id && last.winner_id === last.participant2_id && 'text-live font-bold')}>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Uitdager</div>
              <div className="text-lg">
                <ParticipantName name={participantMap[last.participant2_id] || '?'} href={participantHrefs[last.participant2_id]} />
              </div>
            </div>
          </div>
          {last.score && <div className="text-center text-sm text-muted-foreground mt-2 font-mono tabular-nums">{last.score}</div>}
        </CardContent>
      </Card>

      {/* Volgende uitdager */}
      {isAdmin && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-muted-foreground">
            {lastDone
              ? 'Match afgesloten — roep de volgende uitdager op.'
              : 'Wacht tot deze match klaar is voor de volgende uitdager.'}
          </div>
          <Button size="sm" disabled={!lastDone || busy}
                  variant={lastDone ? 'default' : 'outline'}
                  onClick={callNext}>
            <ChevronRight className="h-4 w-4 mr-1" />
            {busy ? 'Bezig…' : 'Volgende uitdager'}
          </Button>
        </div>
      )}
      {error && <div className="text-sm text-signal">{error}</div>}

      {/* Historiek */}
      {sorted.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
              Historiek
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {sorted.slice(0, -1).reverse().map(m => (
                <div key={m.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded border border-border/40">
                  <CheckCircle2 className="h-3 w-3 text-live" />
                  <span className="w-8 text-muted-foreground font-mono">#{m.match_number}</span>
                  <ParticipantName name={participantMap[m.participant1_id] || '?'} href={participantHrefs[m.participant1_id]}
                                   className={cn('flex-1 truncate', !!m.winner_id && m.winner_id === m.participant1_id && 'font-semibold text-live')} />
                  <span className="text-muted-foreground">vs</span>
                  <ParticipantName name={participantMap[m.participant2_id] || '?'} href={participantHrefs[m.participant2_id]}
                                   className={cn('flex-1 truncate text-right', !!m.winner_id && m.winner_id === m.participant2_id && 'font-semibold text-live')} />
                  {m.score && <span className="font-mono tabular-nums text-muted-foreground w-16 text-right">{m.score}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
